import React, { useEffect } from 'react';
import { Alert, StyleSheet, View, TextInput, Dimensions, ActivityIndicator, Text } from 'react-native';
import Button from '../ui/Button';
import AccountApi from "../../services/modules/account/api";
import { useDispatch, useSelector } from 'react-redux';
import { loginFailure, loginSuccess, updateFirstTime } from '../../services/modules/auth/actions';
import AuthApi from '../../services/modules/auth/api';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Colors from '../../constants/Colors';
import { TraxxText } from '../ui/TraxxText';
import { TraxxTextInput } from '../ui/TraxxTextInput';
import ReCAPTCHA from 'react-google-recaptcha';
import { RootState } from '../../services/store';
import OrganizationApi from '../../services/modules/organization/api';
import { createOrganization } from '../../services/helpers';

interface Props {
  role: string;
  onComplete?: () => void;
}

const HaulerSignUpForm: React.FC<Props> = ({ 
  role,
  onComplete
}) => { 
  const paymentState = useSelector((state: RootState) => state.payment)

  const dispatch = useDispatch();

  const [email, setEmail] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [recaptchaToken, setRecaptchaToken] = React.useState('');
  const [organization, setOrganization] = React.useState(createOrganization())

  const onRecaptchaChange = (value: any) => {
    setRecaptchaToken(value);
  }
  
  const signUp = () => {
    if (recaptchaToken.length == 0) {
      alert("Please check the Recaptcha.")
      return;
    }

    if (password.trim().length == 0 || firstName.trim().length == 0 || lastName.trim().length == 0 || email.trim().length == 0) {
      alert("Please fill in all fields");
      return;
    }

    if (password != confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setSaving(true);

    AccountApi.register(email, password, firstName, lastName, role, recaptchaToken, organization.id, paymentState.inviteId)
    .then(() => {      
      AuthApi.login(email, password)
      .then(async (apiResponse: any) => {
        await storeAutoLogin(apiResponse.data);
        
        dispatch(updateFirstTime(true))
        dispatch(loginSuccess(apiResponse.data));
        
        if (onComplete) {
          onComplete();
        }
      })
      .catch((e) => {
        dispatch(loginFailure());
        setSaving(false);
      });
    })
    .catch((error) => {
      setSaving(false);
      alert(error.data.message);
    });
  };

  const storeAutoLogin = (async (userData: any) => {
    try {
      await AsyncStorage.setItem(
        'user.id',
        userData.id
      );
      await AsyncStorage.setItem(
        'user.firstName',
        userData.firstName
      );
      await AsyncStorage.setItem(
        'user.lastName',
        userData.lastName
      );
      await AsyncStorage.setItem(
        'user.role',
        userData.role
      );
      await AsyncStorage.setItem(
        'user.token',
        userData.token
      );
      await AsyncStorage.setItem(
        'user.refreshToken',
        userData.refreshToken
      );  

      if (userData.profilePhoto == null) {
        await AsyncStorage.setItem(
          'user.profilePhoto',
          ""
        );  
      } else {
        await AsyncStorage.setItem(
          'user.profilePhoto',
          userData.profilePhoto
        );  
      }

      if (userData.vehicleId == null) {
        await AsyncStorage.setItem(
          'user.vehicleId',
          ""
        ); 
      } else {
        await AsyncStorage.setItem(
          'user.vehicleId',
          userData.vehicleId
        ); 
      } 

      const organizationId = parseInt(userData.organizationId).toString();

      await AsyncStorage.setItem(
        'user.organizationId',
        organizationId
      ); 

      if (userData.organization == null) {
        await AsyncStorage.setItem(
          'user.organization',
          ""
        ); 
      } else {
        await AsyncStorage.setItem(
          'user.organization',
          userData.organization
        ); 
      } 

      const metric = parseInt(userData.metric).toString();

      await AsyncStorage.setItem(
        'user.metric',
        metric
      ); 

      await AsyncStorage.setItem(
        'user.subscribed',
        userData.subscribed.toString()
      ); 

      await AsyncStorage.setItem(
        'user.subscriptionOwner',
        userData.subscriptionOwner.toString()
      ); 

      await AsyncStorage.setItem(
        'user.subscriptionId',
        userData.subscriptionId.toString()
      ); 

      return true;
    } catch(error) {
      return false;
    }
  });
  
  useEffect(() => {
    if (paymentState.code.length > 0) {
      OrganizationApi.loadByCode(paymentState.code)
      .then((apiResponse: any) => {
        if (apiResponse.data != null) {
          setOrganization(apiResponse.data)
        }
      })
    }
  }, [paymentState])
  return (
    <View style={styles.container}>
      <View style={styles.inputContainer}>
        {saving &&
            <View>
              <ActivityIndicator size="large" color="#3F51B5" />
              <TraxxText style={styles.searchingText}>Saving...</TraxxText>
            </View>
        }   

        {!saving && <View style={{marginBottom: 50, width: "100%"}}>  
          {organization.id != 0 && <TraxxText style={{padding: 20, fontSize: 18, textAlign: 'center', fontWeight: 'bold'}}>You are signing up to be part of {organization.title}.</TraxxText>}
          <TraxxText style={styles.title}>Please provide your name, email and set a password for your account.</TraxxText>
          
          <TraxxTextInput
            style={styles.input}
            placeholder="Enter your first name"
            placeholderTextColor="#003f5c"
            value={firstName}
            onChangeText={(firstName: string) => setFirstName(firstName)}
            returnKeyType={'next'}
          />
          
          <TraxxTextInput
            style={styles.input}
            placeholder="Enter your last name"
            placeholderTextColor="#003f5c"
            value={lastName}
            onChangeText={(lastName: string) => setLastName(lastName)}
            returnKeyType={'next'}
          />

          <TraxxTextInput
            style={styles.input}
            placeholder="Enter your email"
            placeholderTextColor="#003f5c"
            keyboardType="email-address"
            value={email}
            onChangeText={(email: string) => setEmail(email)}
            returnKeyType={'next'}
          />

          <TraxxTextInput
            style={styles.input}
            placeholder="Enter a password"
            placeholderTextColor="#003f5c"
            secureTextEntry={true}
            value={password}
            onChangeText={(password: string) => setPassword(password)}
            returnKeyType={'next'}
          />
          
          <TraxxText style={styles.hint}>Password must be at least 8 characters and contain one uppercase letter, one lower case letter, a number and a special character.</TraxxText>
          
          <TraxxTextInput
            style={styles.input}
            placeholder="Confirm your password"
            placeholderTextColor="#003f5c"
            secureTextEntry={true}
            value={confirmPassword}
            onChangeText={(confirmPassword: string) => setConfirmPassword(confirmPassword)}
          />
          
          <View style={{alignSelf: 'center'}}>
            <ReCAPTCHA
              sitekey="6LcP5QwhAAAAAJE-hIUahNuoVWPtFyCW6KAeX3w0"
              onChange={onRecaptchaChange}
            />
          </View>
          
          <Button 
            style={styles.button}
            onPress={signUp}
            text="Sign Up">
          </Button>
        </View>}
      </View>
    </View>
  )
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    width: "100%",
    marginTop: 20
  },

  inputContainer: {
    width: "100%",
  },

  hint: {
    fontSize: 14,
    fontStyle: 'italic',
    marginHorizontal: 20
  },
  
  title: {
    fontSize: 20,
    textAlign: 'center',
  },

  searchingText: {
    textAlign: 'center',
    padding: 10,
    fontWeight: 'bold'
  },

  input: {
    height: 50,
    width: "100%",
    paddingVertical: 10,
    paddingHorizontal: 20,
    margin: 10,
    borderColor: Colors.borderGrey,
    borderWidth: 1,
    borderRadius: 20
  },

  button : {
    marginTop: 20,
    width: "100%",
    marginLeft: 10,
  },
});

export default HaulerSignUpForm